import React, { FC } from 'react'
import NextHead from 'next/head'

interface MetaTag {
  name?: string
  property?: string
  content?: string
  override?: string
}

type MetaInput = MetaTag[] | MetaTag | Record<string, MetaTag> | string | null | undefined

interface MetaTagsProps {
  meta?: MetaInput
}

const isMetaTag = (value: unknown): value is MetaTag => {
  if (!value || typeof value !== 'object') return false
  const tag = value as Record<string, unknown>
  const hasNameOrProperty = typeof tag.name === 'string' || typeof tag.property === 'string'
  if (!hasNameOrProperty) return false
  const contentOk = tag.content === undefined || typeof tag.content === 'string'
  const overrideOk = tag.override === undefined || typeof tag.override === 'string'
  return contentOk && overrideOk
}

const normalizeMeta = (meta: MetaInput): MetaTag[] => {
  if (!meta) return []

  if (Array.isArray(meta)) {
    return meta.filter(isMetaTag)
  }

  if (isMetaTag(meta)) {
    return [meta]
  }

  if (typeof meta === 'object') {
    return Object.values(meta as Record<string, unknown>).filter(isMetaTag)
  }

  return []
}

const MetaTags: FC<MetaTagsProps> = ({ meta }) => {
  const tags = normalizeMeta(meta)

  if (tags.length === 0) return null

  return (
    <NextHead>
      {tags.map((tag, index) => {
        const content = tag.override && tag.override.trim() !== '' ? tag.override : tag.content ?? ''
        const key = tag.name ?? tag.property ?? `meta-${index}`

        if (tag.name) {
          return <meta name={tag.name} content={content} key={key} />
        }

        if (tag.property) {
          return <meta property={tag.property} content={content} key={key} />
        }

        return null
      })}
    </NextHead>
  )
}

export default MetaTags

interface LinkRelProps {
  rel: string
  href: string
  crossOrigin?: string
  type?: string
  as?: string
}

export const LinkRel: FC<LinkRelProps> = ({ rel, href, crossOrigin, type, as }) => {
  let url = ''

  if (rel === 'canonical' && href.indexOf('?') >= 0) {
    // rimuove eventuali parametri querystring
    url = href.split('?')[0] ?? ''
  } else {
    url = href
  }

  return (
    <NextHead>
      <link rel={rel} href={url} crossOrigin={crossOrigin || null} type={type || null} as={as || null} />
    </NextHead>
  )
}
